import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLang } from "../lib/lang-context";
import { ArrowUpRight } from "@phosphor-icons/react";
import Reveal from "./Reveal";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CATEGORY_IMAGES = {
  // Medical professional wearing blue nitrile gloves
  gloves:
    "https://images.pexels.com/photos/4226219/pexels-photo-4226219.jpeg?auto=compress&cs=tinysrgb&w=900",
  // Close-up of disposable syringes with vaccine vials
  syringes:
    "https://images.pexels.com/photos/5863396/pexels-photo-5863396.jpeg?auto=compress&cs=tinysrgb&w=900",
  // Healthcare professional wrapping a patient's hand with gauze
  gauze:
    "https://images.pexels.com/photos/5721552/pexels-photo-5721552.jpeg?auto=compress&cs=tinysrgb&w=900",
  // Doctor in scrubs holding a surgical face mask
  masks:
    "https://images.pexels.com/photos/4930708/pexels-photo-4930708.jpeg?auto=compress&cs=tinysrgb&w=900",
  // IV drip in a sterile hospital setting
  iv: "https://images.pexels.com/photos/4421486/pexels-photo-4421486.jpeg?auto=compress&cs=tinysrgb&w=900",
  // Sterile surgical instruments laid out on blue drapes
  sutures:
    "https://images.pexels.com/photos/28727570/pexels-photo-28727570.jpeg?auto=compress&cs=tinysrgb&w=900",
};

export default function Catalog() {
  const { t, lang } = useLang();
  const [items, setItems] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/products`)
      .then((res) => setItems(res.data.categories || []))
      .catch(() => setItems([]));
  }, []);

  return (
    <section
      id="products"
      data-testid="catalog-section"
      className="py-24 md:py-32 bg-slate-50 dark:bg-zinc-900 border-t border-slate-200 dark:border-zinc-800"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <Reveal className="max-w-3xl mb-16">
          <p className="text-xs tracking-[0.24em] uppercase font-bold text-[var(--icor-blue)] eyebrow-line">
            {t.catalog.eyebrow}
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-semibold tracking-tighter mt-5 text-[var(--icor-navy)] dark:text-white text-balance">
            {t.catalog.title}
          </h2>
          <p className="mt-5 text-lg text-slate-600 dark:text-zinc-400 leading-relaxed">
            {t.catalog.subtitle}
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((cat, i) => {
            const name = lang === "es" ? cat.name_es : cat.name_en;
            const desc = lang === "es" ? cat.desc_es : cat.desc_en;
            return (
              <Reveal key={cat.id} delay={i * 80} className="h-full">
              <a
                href="#quote"
                data-testid={`catalog-card-${cat.id}`}
                className="card-lift group bg-white dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 overflow-hidden flex flex-col h-full"
                style={{ borderRadius: 4 }}
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-slate-100 dark:bg-zinc-800">
                  <img
                    src={CATEGORY_IMAGES[cat.id]}
                    alt={name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    loading="lazy"
                  />
                  <div className="absolute top-3 left-3 text-[10px] tracking-[0.2em] uppercase font-bold text-white bg-black/70 backdrop-blur-sm px-2.5 py-1">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                </div>
                <div className="p-6 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-display text-2xl font-medium tracking-tight text-[var(--icor-navy)] dark:text-white">
                      {name}
                    </h3>
                    <ArrowUpRight
                      size={20}
                      className="text-slate-400 dark:text-zinc-500 group-hover:text-[var(--icor-blue)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all"
                    />
                  </div>
                  <p className="mt-3 text-sm text-slate-600 dark:text-zinc-400 leading-relaxed flex-1">
                    {desc}
                  </p>
                  <div className="mt-5 pt-5 border-t border-slate-100 dark:border-zinc-800 text-xs uppercase tracking-[0.18em] font-semibold text-[var(--icor-blue)]">
                    {t.catalog.cta} →
                  </div>
                </div>
              </a>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
}
